import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { SpectatorMap } from '@/components/map';
import { HuntDeletedModal } from '@/components/common';
import { ConnectionStatusBanner } from '@/components/common/ConnectionStatus';
import { useSignalR, HuntCompletedMessage, ItemFoundMessage, PinDiscoveredMessage, ChallengeEncounteredMessage, ChallengeSolvedMessage, HuntDeletedMessage } from '@/hooks/useSignalR';
import { useLocationStore, useHunterLocations } from '@/stores/locationStore';
import { useHuntStore } from '@/stores/huntStore';
import { huntService } from '@/services/api/huntService';
import { pinService } from '@/services/api/pinService';
import { Pin } from '@gps-hunt/shared';

const AZURE_MAPS_KEY = import.meta.env.VITE_AZURE_MAPS_KEY || '';

interface ActivityEvent {
  id: string;
  timestamp: Date;
  message: string;
  type: 'discovery' | 'item' | 'challenge' | 'solved' | 'info';
}

/**
 * Spectator page — passive live view of the hunt.
 * No GPS needed. Reuses SpectatorMap with SignalR location feed.
 */
export const SpectatorPage: React.FC = () => {
  const { huntId } = useParams<{ huntId: string }>();
  const navigate = useNavigate();

  const { clearLocations } = useLocationStore();
  const participantLocations = useHunterLocations();
  const resetHuntStore = useHuntStore((s) => s.reset);

  const [pins, setPins] = useState<Pin[]>([]);
  const [currentPinIndex, setCurrentPinIndex] = useState(0);
  const [completedPinIds, setCompletedPinIds] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [huntDeletedInfo, setHuntDeletedInfo] = useState<{ huntName: string; message: string } | null>(null);
  const [activity, setActivity] = useState<ActivityEvent[]>([]);
  const [showActivity, setShowActivity] = useState(false);

  const huntIdRef = useRef(huntId);
  useEffect(() => { huntIdRef.current = huntId; }, [huntId]);

  // Helpers
  const addActivity = useCallback((message: string, type: ActivityEvent['type']) => {
    setActivity((prev) => [
      { id: `${Date.now()}-${Math.random()}`, timestamp: new Date(), message, type },
      ...prev,
    ].slice(0, 50));
  }, []);

  // ── SignalR handlers ──
  const handleHuntDeleted = useCallback((msg: HuntDeletedMessage) => {
    resetHuntStore();
    setHuntDeletedInfo({ huntName: msg.huntName, message: msg.message });
  }, [resetHuntStore]);

  const handleHuntCompleted = useCallback((_msg: HuntCompletedMessage) => {
    const hId = huntIdRef.current;
    if (hId) navigate(`/stats/${hId}`, { replace: true });
  }, [navigate]);

  const handlePinDiscovered = useCallback((msg: PinDiscoveredMessage) => {
    addActivity(`${msg.hunterName} discovered Pin ${msg.pinIndex + 1}!`, 'discovery');
    setCurrentPinIndex(msg.pinIndex);
  }, [addActivity]);

  const handleItemFound = useCallback((msg: ItemFoundMessage) => {
    addActivity(`Item found at pin! (${msg.totalFound} total)`, 'item');
  }, [addActivity]);

  const handleChallengeEncountered = useCallback((msg: ChallengeEncounteredMessage) => {
    addActivity(`Challenge encountered — captain: ${msg.captainDisplayName}`, 'challenge');
  }, [addActivity]);

  const handleChallengeSolved = useCallback((msg: ChallengeSolvedMessage) => {
    addActivity(`Challenge solved by ${msg.solvedByDisplayName}! 🎉`, 'solved');
    // After challenge solved, pin will be completed — increment index
    setCurrentPinIndex((prev) => prev + 1);
    if (msg.pinId) {
      setCompletedPinIds((prev) => prev.includes(msg.pinId) ? prev : [...prev, msg.pinId]);
    }
  }, []);

  // ── SignalR connection ──
  const {
    connectionStatus,
    connect: connectSignalR,
    disconnect: disconnectSignalR,
    forceReconnect,
  } = useSignalR({
    onHuntDeleted: handleHuntDeleted,
    onHuntCompleted: handleHuntCompleted,
    onPinDiscovered: handlePinDiscovered,
    onItemFound: handleItemFound,
    onChallengeEncountered: handleChallengeEncountered,
    onChallengeSolved: handleChallengeSolved,
  });

  // ── Initialize ──
  useEffect(() => {
    if (!huntId) return;

    const init = async () => {
      try {
        setIsLoading(true);
        // Load pins (spectator sees hider view — all pins)
        const huntData = await huntService.getHunt(huntId);
        if (!huntData) throw new Error('Hunt not found');

        const pinData = await pinService.getPins(huntId);
        setPins(pinData.pins as Pin[]);

        connectSignalR(huntId);
        addActivity('Connected to live hunt feed', 'info');
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load hunt');
      } finally {
        setIsLoading(false);
      }
    };

    init();

    return () => {
      disconnectSignalR();
      clearLocations();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [huntId]);

  if (huntDeletedInfo) {
    return (
      <HuntDeletedModal
        huntName={huntDeletedInfo.huntName}
        message={huntDeletedInfo.message}
        onDismiss={() => navigate('/welcome', { replace: true })}
      />
    );
  }

  if (isLoading) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="loading-spinner" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="h-full flex flex-col items-center justify-center p-6 space-y-4">
        <p className="text-red-400">{error}</p>
        <button onClick={() => navigate('/welcome', { replace: true })} className="text-primary underline">
          Back to Home
        </button>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col overflow-hidden">
      {/* Connection banner */}
      <ConnectionStatusBanner
        status={connectionStatus}
        onRetry={forceReconnect}
      />

      {/* Map */}
      <div className="flex-1 relative">
        <SpectatorMap
          subscriptionKey={AZURE_MAPS_KEY}
          pins={pins}
          activePinIndex={currentPinIndex}
          completedPinIds={completedPinIds}
          participants={participantLocations.map((p) => ({
            userId: p.userId,
            displayName: p.displayName,
            latitude: p.latitude,
            longitude: p.longitude,
            role: p.role as 'hunter' | 'hider',
          }))}
          className="w-full h-full"
        />

        {/* Top bar: stats */}
        <div className="absolute top-4 left-4 right-4 flex items-center justify-between pointer-events-none">
          <div className="bg-surface/90 backdrop-blur rounded-lg px-3 py-2 pointer-events-auto flex items-center gap-4 text-xs text-text-secondary">
            <span>Pins: <strong className="text-text-primary">{currentPinIndex}/{pins.length}</strong></span>
            <span>Hunters: <strong className="text-text-primary">{participantLocations.filter((p) => p.role === 'hunter').length}</strong></span>
          </div>

          {/* Activity feed toggle */}
          <button
            onClick={() => setShowActivity(!showActivity)}
            className="bg-surface/90 backdrop-blur rounded-lg px-3 py-2 pointer-events-auto text-xs text-text-secondary hover:text-text-primary transition-colors"
          >
            📋 {activity.length > 0 && <span className="text-primary ml-1">{activity.length}</span>}
          </button>
        </div>

        {/* Activity drawer */}
        {showActivity && (
          <div className="absolute bottom-0 left-0 right-0 max-h-[40%] bg-surface/95 backdrop-blur border-t border-surface-light overflow-auto">
            <div className="p-3 space-y-1.5">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-semibold text-text-primary">Activity Feed</h3>
                <button onClick={() => setShowActivity(false)} className="text-text-secondary text-xs">Close</button>
              </div>
              {activity.length === 0 ? (
                <p className="text-text-secondary text-xs">No activity yet…</p>
              ) : (
                activity.map((evt) => (
                  <div key={evt.id} className="flex items-start gap-2 text-xs">
                    <span className="text-text-secondary flex-shrink-0">
                      {evt.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </span>
                    <span className={
                      evt.type === 'solved' ? 'text-green-400' :
                      evt.type === 'challenge' ? 'text-amber-400' :
                      evt.type === 'discovery' ? 'text-blue-400' :
                      'text-text-secondary'
                    }>
                      {evt.message}
                    </span>
                  </div>
                ))
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SpectatorPage;
